/**
 * Growth Lanes Page
 *
 * Lane-keyed funnel analytics: shows sessions/emails/payments/revenue/spend/CPA/ROAS
 * per canonical growth lane (GLP-1, Phentermine, TRT, Hair, etc.).
 * Includes data quality indicators and confidence scoring.
 */

import { useState, useEffect, useCallback } from 'react'
import { format, subDays } from 'date-fns'
import { lanesAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button
} from '../components/ui'
import { RefreshCw, Loader2, AlertCircle, DollarSign, Target, BarChart3, ShoppingBag, AlertTriangle, ChevronRight, ChevronDown, ArrowUp, ArrowDown, GitCompare } from 'lucide-react'
import NoAdSpendNotice from '../components/NoAdSpendNotice'

function getDefaultDateRange() {
  const end = new Date()
  const start = subDays(end, 29)
  return { from: start, to: end }
}

function formatNumber(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US').format(value)
}

function formatCurrency(dollars) {
  if (dollars === null || dollars === undefined || isNaN(dollars)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(dollars)
}

function formatPct(value) {
  if (value === null || value === undefined || isNaN(value)) return '-'
  return `${value}%`
}

export default function LanesPage() {
  const { currentClinicId } = useAuthStore()
  const [dateRange, setDateRange] = useState(getDefaultDateRange)
  const [data, setData] = useState(null)
  const [priorData, setPriorData] = useState(null)
  const [compareMode, setCompareMode] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedLanes, setExpandedLanes] = useState(new Set())
  const [sortKey, setSortKey] = useState('lp_visitors')
  const [sortDir, setSortDir] = useState('desc')

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const start = format(dateRange.from, 'yyyy-MM-dd')
      const end = format(dateRange.to, 'yyyy-MM-dd')

      const params = { start, end, includeLPBreakdown: true }

      const fetches = [lanesAPI.getFunnel(params)]

      // If compare mode, also fetch prior period
      if (compareMode) {
        const periodMs = dateRange.to.getTime() - dateRange.from.getTime()
        const priorEnd = new Date(dateRange.from.getTime() - 86400000) // day before start
        const priorStart = new Date(priorEnd.getTime() - periodMs)
        fetches.push(
          lanesAPI.getFunnel({
            start: format(priorStart, 'yyyy-MM-dd'),
            end: format(priorEnd, 'yyyy-MM-dd'),
            includeLPBreakdown: false,
          })
        )
      }

      const results = await Promise.all(fetches)

      if (results[0].data.success) {
        setData(results[0].data.data)
      }
      if (results[1]?.data?.success) {
        // Build lane → metrics map for quick lookup
        const priorMap = {}
        for (const lane of results[1].data.data.lanes || []) {
          priorMap[lane.lane] = lane
        }
        priorMap._summary = results[1].data.data.summary
        setPriorData(priorMap)
      } else {
        setPriorData(null)
      }
    } catch (err) {
      console.error('[LANES] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [dateRange, currentClinicId, compareMode])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const toggleLane = (laneId) => {
    setExpandedLanes(prev => {
      const next = new Set(prev)
      if (next.has(laneId)) next.delete(laneId)
      else next.add(laneId)
      return next
    })
  }

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sortRows = (rows) => {
    if (!rows) return []
    return [...rows].sort((a, b) => {
      const av = a[sortKey] ?? -Infinity
      const bv = b[sortKey] ?? -Infinity
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      return sortDir === 'asc' ? av - bv : bv - av
    })
  }

  const sortedLanes = sortRows(data?.lanes)

  const summary = data?.summary
  const quality = data?.quality

  return (
    <div className="space-y-6">
      <NoAdSpendNotice />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Growth Lanes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Lane-keyed funnel — sessions to revenue by product lane
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="py-4 px-4">
          <div className="flex items-center gap-3">
            <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
            <Button
              variant={compareMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCompareMode(v => !v)}
              className="whitespace-nowrap"
            >
              <GitCompare className="h-4 w-4 mr-1.5" />
              {compareMode ? 'Comparing' : 'Compare'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card>
          <CardContent className="py-6">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
              <p className="text-destructive">{error}</p>
              <Button variant="outline" onClick={fetchData} className="mt-3">Try Again</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p>Analyzing lane funnels...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {!loading && !error && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Revenue</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(summary.total_revenue)}</p>
              {priorData?._summary && <DeltaIndicator current={summary.total_revenue} prior={priorData._summary.total_revenue} />}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-blue-500" />
                <span className="text-xs text-muted-foreground">Ad Spend</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(summary.total_spend)}</p>
              {priorData?._summary && <DeltaIndicator current={summary.total_spend} prior={priorData._summary.total_spend} invert />}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-4 w-4 text-amber-500" />
                <span className="text-xs text-muted-foreground">CPA</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(summary.overall_cpa)}</p>
              {priorData?._summary && <DeltaIndicator current={summary.overall_cpa} prior={priorData._summary.overall_cpa} invert />}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="h-4 w-4 text-green-500" />
                <span className="text-xs text-muted-foreground">ROAS</span>
              </div>
              <p className={`text-2xl font-bold ${summary.overall_roas >= 1 ? 'text-green-600' : 'text-red-500'}`}>
                {summary.overall_roas?.toFixed(2)}x
              </p>
              {priorData?._summary && <DeltaIndicator current={summary.overall_roas} prior={priorData._summary.overall_roas} />}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Payments</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(summary.total_payments)}</p>
              {priorData?._summary && <DeltaIndicator current={summary.total_payments} prior={priorData._summary.total_payments} />}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Mapped</span>
              </div>
              <p className="text-2xl font-bold">{summary.mapped_pct}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Confidence</span>
              </div>
              <p className="text-2xl font-bold">{quality ? Math.round(quality.confidence_score * 100) : 0}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-4">
              <div className="flex items-center gap-2 mb-1">
                <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Lanes</span>
              </div>
              <p className="text-2xl font-bold">{data?.lanes?.filter(l => l.lane !== 'unmapped' && l.lp_visitors > 0).length || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quality Warning */}
      {!loading && !error && quality && (quality.confidence_score < 0.7 || summary?.unmapped_pct > 25) && (
        <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
              <span className="text-sm text-foreground">
                {quality.confidence_score < 0.7 && `Low confidence (${Math.round(quality.confidence_score * 100)}%) — many users classified by product fallback rather than page path. `}
                {summary?.unmapped_pct > 25 && `High unmapped rate (${summary.unmapped_pct}%) — consider adding page/product patterns to lane-mapping.json.`}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reconciliation Warning */}
      {!loading && !error && summary?.reconciliation_warning && (
        <Card className="border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />
              <span className="text-sm text-foreground">{summary.reconciliation_warning}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lane Funnel Table */}
      {!loading && !error && data?.lanes?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Lane Funnel</CardTitle>
            <CardDescription>
              Funnel metrics per growth lane — {data.meta?.attribution === 'last_touch' ? 'last-touch' : 'first-touch'} attribution
              {data.meta?.mapping_version && ` (mapping v${data.meta.mapping_version})`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <SortHeader label="Lane" sortKey="display_name" align="left" current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortHeader label="LP Visitors" sortKey="lp_visitors" current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortHeader label="Intakes" sortKey="intake_starts" current={sortKey} dir={sortDir} onSort={handleSort} className="hidden sm:table-cell" />
                    <SortHeader label="Emails" sortKey="emails" current={sortKey} dir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
                    <SortHeader label="Plans" sortKey="plans_selected" current={sortKey} dir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
                    <SortHeader label="Payments" sortKey="payments" current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortHeader label="Visit→Pay" sortKey="visit_to_pay" current={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortHeader label="Revenue" sortKey="revenue" current={sortKey} dir={sortDir} onSort={handleSort} className="hidden sm:table-cell" />
                    <SortHeader label="Spend" sortKey="ad_spend" current={sortKey} dir={sortDir} onSort={handleSort} className="hidden lg:table-cell" />
                    <SortHeader label="CPA" sortKey="cpa" current={sortKey} dir={sortDir} onSort={handleSort} className="hidden lg:table-cell" />
                    <SortHeader label="ROAS" sortKey="roas" current={sortKey} dir={sortDir} onSort={handleSort} className="hidden lg:table-cell" />
                    <SortHeader label="LP→Intake" sortKey="lp_to_intake" current={sortKey} dir={sortDir} onSort={handleSort} className="hidden xl:table-cell" />
                    <SortHeader label="Intake→Email" sortKey="intake_to_email" current={sortKey} dir={sortDir} onSort={handleSort} className="hidden xl:table-cell" />
                    <SortHeader label="Email→Plan" sortKey="email_to_plan" current={sortKey} dir={sortDir} onSort={handleSort} className="hidden xl:table-cell" />
                    <SortHeader label="Plan→Pay" sortKey="plan_to_payment" current={sortKey} dir={sortDir} onSort={handleSort} className="hidden xl:table-cell" />
                  </tr>
                </thead>
                <tbody>
                  {sortedLanes.map((lane) => {
                    const isUnmapped = lane.lane === 'unmapped'
                    const hasLPs = lane.lp_breakdown?.length > 0
                    const isExpanded = expandedLanes.has(lane.lane)

                    return (
                      <LaneRowGroup
                        key={lane.lane}
                        lane={lane}
                        isUnmapped={isUnmapped}
                        hasLPs={hasLPs}
                        isExpanded={isExpanded}
                        onToggle={() => toggleLane(lane.lane)}
                        sortKey={sortKey}
                        sortDir={sortDir}
                        priorLane={priorData?.[lane.lane] || null}
                      />
                    )
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lane Revenue Distribution */}
      {!loading && !error && data?.lanes?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Revenue by Lane</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.lanes
                .filter(l => l.revenue > 0)
                .map((lane) => {
                  const totalRev = summary?.total_revenue || 1
                  const pct = Math.round(lane.revenue / totalRev * 1000) / 10
                  return (
                    <div key={lane.lane} className="flex items-center gap-3">
                      <div className="flex items-center gap-2 w-28 flex-shrink-0">
                        <LaneDot lane={lane.lane} />
                        <span className="text-sm font-medium truncate">{lane.display_name}</span>
                      </div>
                      <div className="flex-1 bg-muted rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full rounded-full flex items-center px-2"
                          style={{
                            width: `${Math.max(pct, 2)}%`,
                            backgroundColor: LANE_COLORS[lane.lane] || '#6B7280',
                          }}
                        >
                          {pct > 10 && <span className="text-xs text-white font-medium">{formatCurrency(lane.revenue)}</span>}
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground w-14 text-right">{pct}%</span>
                    </div>
                  )
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!loading && !error && (!data?.lanes || data.lanes.length === 0) && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <p>No lane data found for this date range.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function DeltaIndicator({ current, prior, invert = false, format: fmt }) {
  if (prior == null || prior === 0 || current == null) return null
  const pct = ((current - prior) / prior) * 100
  if (!isFinite(pct)) return null
  const isPositive = pct > 0
  const isGood = invert ? !isPositive : isPositive
  const absVal = Math.abs(pct)
  return (
    <div className={`text-[10px] font-medium leading-tight ${isGood ? 'text-green-600' : 'text-red-500'}`}>
      {isPositive ? '↑' : '↓'}{absVal < 100 ? absVal.toFixed(1) : Math.round(absVal)}%
      {fmt && <span className="text-muted-foreground ml-0.5">({fmt(prior)})</span>}
    </div>
  )
}

function LaneRowGroup({ lane, isUnmapped, hasLPs, isExpanded, onToggle, sortKey, sortDir, priorLane }) {
  const lpSortKey = sortKey === 'display_name' ? 'landing_page' : sortKey
  const sortedLPs = isExpanded && lane.lp_breakdown ? [...lane.lp_breakdown].sort((a, b) => {
    const av = a[lpSortKey] ?? -Infinity
    const bv = b[lpSortKey] ?? -Infinity
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
    return sortDir === 'asc' ? av - bv : bv - av
  }) : []

  return (
    <>
      <tr
        className={`border-t hover:bg-muted/30 ${isUnmapped ? 'text-muted-foreground bg-muted/10' : ''} ${hasLPs ? 'cursor-pointer' : ''}`}
        onClick={hasLPs ? onToggle : undefined}
      >
        <td className="p-3 font-medium whitespace-nowrap">
          <div className="flex items-center gap-2">
            {hasLPs ? (
              isExpanded
                ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            ) : (
              <span className="w-3.5 flex-shrink-0" />
            )}
            <LaneDot lane={lane.lane} />
            {lane.display_name}
            {lane.spend_allocation_mode === 'modeled' && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1 rounded">modeled</span>
            )}
          </div>
        </td>
        <td className="p-3 text-right font-mono">
          {formatNumber(lane.lp_visitors)}
          {priorLane && <DeltaIndicator current={lane.lp_visitors} prior={priorLane.lp_visitors} />}
        </td>
        <td className="p-3 text-right font-mono hidden sm:table-cell">
          {formatNumber(lane.intake_starts)}
          {priorLane && <DeltaIndicator current={lane.intake_starts} prior={priorLane.intake_starts} />}
        </td>
        <td className="p-3 text-right font-mono hidden md:table-cell">
          {formatNumber(lane.emails)}
          {priorLane && <DeltaIndicator current={lane.emails} prior={priorLane.emails} />}
        </td>
        <td className="p-3 text-right font-mono hidden md:table-cell">
          {formatNumber(lane.plans_selected)}
          {priorLane && <DeltaIndicator current={lane.plans_selected} prior={priorLane.plans_selected} />}
        </td>
        <td className="p-3 text-right font-mono">
          {formatNumber(lane.payments)}
          {priorLane && <DeltaIndicator current={lane.payments} prior={priorLane.payments} />}
        </td>
        <td className="p-3 text-right font-mono">
          {lane.visit_to_pay != null ? `${lane.visit_to_pay}%` : <span className="text-muted-foreground">-</span>}
          {priorLane && <DeltaIndicator current={lane.visit_to_pay} prior={priorLane.visit_to_pay} />}
        </td>
        <td className="p-3 text-right font-mono hidden sm:table-cell">
          {formatCurrency(lane.revenue)}
          {priorLane && <DeltaIndicator current={lane.revenue} prior={priorLane.revenue} />}
        </td>
        <td className="p-3 text-right font-mono hidden lg:table-cell">
          {lane.ad_spend ? formatCurrency(lane.ad_spend) : <span className="text-muted-foreground">-</span>}
          {priorLane && <DeltaIndicator current={lane.ad_spend} prior={priorLane.ad_spend} invert />}
        </td>
        <td className="p-3 text-right font-mono hidden lg:table-cell">
          {lane.cpa ? formatCurrency(lane.cpa) : <span className="text-muted-foreground">-</span>}
          {priorLane && <DeltaIndicator current={lane.cpa} prior={priorLane.cpa} invert />}
        </td>
        <td className="p-3 text-right font-mono hidden lg:table-cell">
          {lane.roas ? (
            <span className={lane.roas >= 1 ? 'text-green-600' : 'text-red-500'}>
              {lane.roas.toFixed(2)}x
            </span>
          ) : <span className="text-muted-foreground">-</span>}
          {priorLane && <DeltaIndicator current={lane.roas} prior={priorLane.roas} />}
        </td>
        <td className="p-3 text-right font-mono hidden xl:table-cell">{formatPct(lane.lp_to_intake)}</td>
        <td className="p-3 text-right font-mono hidden xl:table-cell">{formatPct(lane.intake_to_email)}</td>
        <td className="p-3 text-right font-mono hidden xl:table-cell">{formatPct(lane.email_to_plan)}</td>
        <td className="p-3 text-right font-mono hidden xl:table-cell">{formatPct(lane.plan_to_payment)}</td>
      </tr>
      {isExpanded && sortedLPs.map((lp, idx) => (
        <tr key={`${lane.lane}-lp-${idx}`} className="border-t bg-muted/10 text-muted-foreground">
          <td className="p-3 pl-12 whitespace-nowrap">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs truncate max-w-[200px]" title={lp.landing_page}>
                {lp.landing_page}
              </span>
              {lp.is_direct_to_intake && (
                <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                  direct
                </span>
              )}
            </div>
          </td>
          <td className="p-3 text-right font-mono">{formatNumber(lp.lp_visitors)}</td>
          <td className="p-3 text-right font-mono hidden sm:table-cell">{formatNumber(lp.intake_starts)}</td>
          <td className="p-3 text-right font-mono hidden md:table-cell">{formatNumber(lp.emails)}</td>
          <td className="p-3 text-right font-mono hidden md:table-cell">{formatNumber(lp.plans_selected)}</td>
          <td className="p-3 text-right font-mono">{formatNumber(lp.payments)}</td>
          <td className="p-3 text-right font-mono">
            {lp.visit_to_pay != null ? `${lp.visit_to_pay}%` : <span className="text-muted-foreground">-</span>}
          </td>
          <td className="p-3 text-right font-mono hidden sm:table-cell">{formatCurrency(lp.revenue)}</td>
          <td className="p-3 text-right font-mono hidden lg:table-cell"><span className="text-muted-foreground">-</span></td>
          <td className="p-3 text-right font-mono hidden lg:table-cell"><span className="text-muted-foreground">-</span></td>
          <td className="p-3 text-right font-mono hidden lg:table-cell"><span className="text-muted-foreground">-</span></td>
          <td className="p-3 text-right font-mono hidden xl:table-cell">{formatPct(lp.lp_to_intake)}</td>
          <td className="p-3 text-right font-mono hidden xl:table-cell">{formatPct(lp.intake_to_email)}</td>
          <td className="p-3 text-right font-mono hidden xl:table-cell">{formatPct(lp.email_to_plan)}</td>
          <td className="p-3 text-right font-mono hidden xl:table-cell">{formatPct(lp.plan_to_payment)}</td>
        </tr>
      ))}
    </>
  )
}

function SortHeader({ label, sortKey: key, align = 'right', current, dir, onSort, className = '' }) {
  const active = current === key
  return (
    <th
      className={`p-3 font-medium whitespace-nowrap select-none cursor-pointer hover:bg-muted/80 text-${align} ${className}`}
      onClick={() => onSort(key)}
    >
      <span className="inline-flex items-center gap-1">
        {align === 'right' && active && (dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
        {label}
        {align === 'left' && active && (dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </span>
    </th>
  )
}

const LANE_COLORS = {
  glp1: '#10B981',
  phentermine: '#3B82F6',
  trt: '#8B5CF6',
  hair: '#F59E0B',
  skin: '#EC4899',
  ed: '#EF4444',
  nad: '#14B8A6',
  unmapped: '#6B7280',
}

function LaneDot({ lane }) {
  return (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
      style={{ backgroundColor: LANE_COLORS[lane] || '#6B7280' }}
    />
  )
}
